import { model as router } from '!/router'
import { model as config, remote } from '@/config'
import {
  formatBannerUrl,
  formatCreatorAvatarUrl,
  formatDuration,
  getMacrosHeader,
  T,
} from '@/helpers'
import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  sample,
  type EventCallable,
  type StoreWritable,
} from 'effector'
import { produce } from 'immer'
import { model as session } from '~/entities/session'
import { googlePalFeatureModel } from '~/features/google-pal'
import { contentTypesWithEpg } from '~/shared/constants'
import { $watchId, generateWatchIdFx } from '~/widgets/player/analytics'
import { type FormattedContent } from './index.h'

/*
 * Single content item
 */

// fetch one content item
export const getOneFx: typeof api.content.base.getOneFx = attach({
  effect: api.content.base.getOneFx,
})

// currently loaded content item
export const $current: StoreWritable<FormattedContent | null> =
  createStore<FormattedContent | null>(null)
    .on(getOneFx.doneData, (_, got) =>
      produce(got as FormattedContent, (draft) => {
        draft.banner = formatBannerUrl(draft.banner || '')
        draft.duration = formatDuration(draft?.duration) ?? ''
        if (draft.creator) {
          draft.creator.avatar = formatCreatorAvatarUrl(
            draft.creator.avatar || ''
          )
        }
      })
    )
    .reset(getOneFx, getOneFx.fail)

// Store for URL that prevents taking previous url during nonce generation.
// Without this store, the old URL could trigger an unnecessary playlist request (that would be cancelled)
// before the new URL with nonce arrives.
export const $url = combine(
  $current,
  googlePalFeatureModel.$idType,
  (current, idType) => {
    // Return current url for non-movie content, when user open live or tvshow there is no need to reset movie url for case if user will open this movie once again
    if (idType?.type !== contentTypesWithEpg.movie) {
      return current?.url ?? null
    }

    // Reset url - return null if content or idType is missing or ids don't match
    const hasValidContent = Boolean(
      current && idType && current.id === idType.id
    )
    if (hasValidContent === false) {
      return null
    }

    return current?.url ?? null
  }
)

export const $pending = getOneFx.pending

export const $isUrlRequestFailed: StoreWritable<boolean> = createStore(false)
  .on(getOneFx.fail, T)
  .reset(getOneFx)

const doRequest: EventCallable<void> = createEvent<void>()

// Load content after watchId is generated
sample({
  clock: generateWatchIdFx.done,
  source: {
    idType: googlePalFeatureModel.$idType,
  },
  filter: ({ idType }) => idType?.type === contentTypesWithEpg.movie,
  target: doRequest,
})

sample({
  clock: doRequest,
  source: {
    idType: googlePalFeatureModel.$idType,
    nonce: googlePalFeatureModel.$nonce,
    macrosHeader: config.get(remote.uvo_macrosHeader),
    requestRandom: $watchId,
    sessionInfo: session.$info,
  },
  fn: ({ idType, nonce, macrosHeader, requestRandom, sessionInfo }) => {
    const userGuid = sessionInfo?.userGuid
    let headers = {}
    if (macrosHeader && requestRandom && userGuid) {
      headers = {
        [macrosHeader]: getMacrosHeader(requestRandom, userGuid, nonce),
      }
    }

    return { id: idType?.id, headers }
  },
  target: getOneFx,
})

export const goToDetails: EventCallable<string> = createEvent()

sample({
  clock: goToDetails,
  target: router.navigatePush,
})
